import React, { useState, useEffect } from "react";
import PermitCheckerDashboard from "./PermitCheckerDashboard";
import PermitMakerDashboard from "./PermitMakerDashboard";
import {
  getCurrentUserId,
  getSafetyInspectionRole,
  getUserData,
  resolveActiveProjectId,
  resolveActiveProjectAccesses,
  resolveProjectRoles,
  resolveSelectedWorkflowRole,
} from "../../../../utils/UserUtils";

const MAKER_SIDE_ROLES = new Set([
  "MAKER",
  "INTIALIZER",
  "INITIALIZER",
  "INITIALISER",
  "SUPERVISOR",
]);

const CHECKER_SIDE_ROLES = new Set(["CHECKER"]);

const normalizeRoleStr = (val) =>
  String(val || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

const checkIsMobileSession = () => {
  if (localStorage.getItem("MOBILE_WEBVIEW_SESSION") === "true") {
    return true;
  }
  const token = localStorage.getItem("token") || localStorage.getItem("ACCESS_TOKEN");
  if (token && typeof token === "string" && token.includes(".")) {
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        if (payload && payload.mobile_webview_session) {
          return true;
        }
      }
    } catch (e) {
      // ignore
    }
  }
  return false;
};

export default function UserPermitDashboard() {
  const userId = getCurrentUserId();
  const userData = getUserData();
  const activeProjectId = resolveActiveProjectId();
  const isMobile = checkIsMobileSession();

  const [targetComponent, setTargetComponent] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (isMobile) {
      if (!activeProjectId) {
        setTargetComponent("unauthorized");
        setResolved(true);
        return;
      }

      const accesses = resolveActiveProjectAccesses(userData, activeProjectId);
      if (!accesses || accesses.length === 0) {
        setTargetComponent("unauthorized");
        setResolved(true);
        return;
      }

      const projectRoles = resolveProjectRoles(userData, activeProjectId);
      const selectedRole = resolveSelectedWorkflowRole(userData, activeProjectId);
      const normSelected = normalizeRoleStr(selectedRole);

      const isValidRoleInProject = projectRoles.some(
        (r) => normalizeRoleStr(r) === normSelected
      );

      if (!isValidRoleInProject && normSelected) {
        setTargetComponent("unauthorized");
        setResolved(true);
        return;
      }

      if (CHECKER_SIDE_ROLES.has(normSelected)) {
        setTargetComponent(<PermitCheckerDashboard activeWorkflowRole={selectedRole} />);
      } else if (MAKER_SIDE_ROLES.has(normSelected)) {
        setTargetComponent(<PermitMakerDashboard activeWorkflowRole={selectedRole} />);
      } else {
        setTargetComponent("unauthorized");
      }

      setResolved(true);
      return;
    }

    // Normal Browser Flow
    const roleFromUtils = resolveSelectedWorkflowRole(userData, activeProjectId) || getSafetyInspectionRole([], userId);
    const normRole = normalizeRoleStr(roleFromUtils);

    if (CHECKER_SIDE_ROLES.has(normRole) || roleFromUtils === "both" || normRole === "CHECKER") {
      setTargetComponent(<PermitCheckerDashboard activeWorkflowRole="CHECKER" />);
    } else {
      setTargetComponent(<PermitMakerDashboard activeWorkflowRole={roleFromUtils || "MAKER"} />);
    }
    setResolved(true);
  }, [isMobile, activeProjectId, userId]);

  if (!resolved) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40">
        <p className="text-sm text-muted-foreground">Loading Permit Dashboard…</p>
      </div>
    );
  }

  if (targetComponent === "unauthorized") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <div className="max-w-md rounded-xl bg-white p-8 shadow-sm border border-slate-200">
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Permit role not selected
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            Open this module using a role assigned to the active project.
          </p>
          <button
            type="button"
            onClick={() => (window.location.href = "/config")}
            className="rounded-lg bg-orange-500 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-600 transition-colors"
          >
            Return to Workspace Config
          </button>
        </div>
      </div>
    );
  }

  return targetComponent;
}
